import { ButtonProps, Button } from '@cad-arc';
import React, { useEffect, useState } from 'react';
import styles from './unread-button.module.css';
import { useAdditionalInfos } from '../../hooks/use-additional-infos/additional-infos-provider';
import { observer } from 'mobx-react-lite';
import { useVirtualizerContext } from '../../additional-info-renderer/use-row-virtualizer';
import classnames from 'classnames';
import { sleep } from '@m43/core';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { SortDirection } from '../../hooks/use-additional-infos/types';

const UnreadArrowButton = ({
    position,
    urgent = false,
    ...rest
}: ButtonProps & {
    children?: React.ReactNode;
    onClick?: (e?: unknown) => void;
    urgent?: boolean;
    position: 'top' | 'bottom';
}): React.JSX.Element => {
    return (
        <Button
            variant="solid"
            trailingVisual={position === 'top' ? 'ArrowUp' : 'ArrowDown'}
            className={classnames(
                position === 'top' ? styles['top-button'] : styles['bottom-button'],
                urgent ? styles['urgent-button'] : styles['not-urgent-button']
            )}
            {...rest}
        />
    );
};

export const UnreadButton = observer(() => {
    const { getVirtualItems, scrollToIndex } = useVirtualizerContext();
    const {
        fetchNextPage,
        getIndexById,
        hasNextPage,
        isFetchingNextPage,
        list,
        pageFetchFailed,
        unreadInfos,
        visiblePinnedEntries,
        showPinnedDivider,
        sortDirection,
    } = useAdditionalInfos();
    const isAsc = sortDirection === SortDirection.ASC;
    const { rollOutCadNarrativeFilteringAndFocusImprovementsEnabled } = useEnabledFeatures();
    const isAscending = sortDirection === SortDirection.ASC;
    const [scrollTargetInfoId, setScrollTargetInfoId] = useState<number | null>(null);

    const scrollToInfoId = async (id: number): Promise<void> => {
        const index = getIndexById(id);
        if (Number.isInteger(index)) {
            // scrollToIndex isn't aware of long infos, so it estimates how far to scroll.
            // Note we don't mark it as read here. If after two scrolls we've still missed it,
            // we don't want to mark it as read. Leave it up to `useUpdateVisibleRead` to handle
            // markAsRead functionality, since it's tied _directly_ to what's visible.
            scrollToIndex(index as number, { align: 'center' });
            await sleep(10);
            scrollToIndex(index as number, { align: 'center' });
        }
        setScrollTargetInfoId(null);
    };

    // this useEffect will load pages until the scroll target info id has been loaded, and then
    // scroll to that row
    useEffect(() => {
        if (scrollTargetInfoId === null || list.length === 0) {
            return;
        }

        const lastLoadedInfoId = list[list.length - 1].id;
        if (scrollTargetInfoId >= lastLoadedInfoId) {
            scrollToInfoId(scrollTargetInfoId);
        } else if (hasNextPage && !isFetchingNextPage && !pageFetchFailed) {
            fetchNextPage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list.length, hasNextPage, isFetchingNextPage, scrollTargetInfoId, pageFetchFailed]);

    const virtualItems = getVirtualItems();

    // Virtual-item indices address `combinedData`, which interleaves pinned
    // rows, the divider, and the chronological `list`. The interleave order
    // is sort-aware:
    //   DESC: [...pinned, ...divider, ...list] — chronological at the bottom.
    //   ASC:  [...list,   ...divider, ...pinned] — chronological at the top.
    // Compute the chronological range in `combinedData` accordingly so we can
    // translate a virtual index back to a `list` index (returns `undefined`
    // when the virtual item is a pinned row or the divider).
    const pinnedRowCount = rollOutCadNarrativeFilteringAndFocusImprovementsEnabled
        ? visiblePinnedEntries.length
        : 0;
    const dividerCount =
        rollOutCadNarrativeFilteringAndFocusImprovementsEnabled && showPinnedDivider ? 1 : 0;
    const chronologicalStartIndex = isAscending ? 0 : pinnedRowCount + dividerCount;
    const chronologicalEndIndex = chronologicalStartIndex + list.length - 1;
    const toListIndex = (virtualIndex: number): number | undefined => {
        if (virtualIndex < chronologicalStartIndex || virtualIndex > chronologicalEndIndex) {
            return undefined;
        }
        return virtualIndex - chronologicalStartIndex;
    };

    const lastVirtualItem = virtualItems[virtualItems.length - 1];
    const firstVirtualItem = virtualItems[0];

    // Flag-off: combinedData === list (no pinned/divider), so preserve main's
    // exact index→item resolution. In particular the bottom loader row (virtual
    // index === list.length) resolves to undefined here, which makes the
    // component render nothing that frame — the clamp-to-last-real-item fallback
    // below is flag-on-only behavior.
    let lastRenderedItem: typeof list[number] | undefined;
    let firstRenderedItem: typeof list[number] | undefined;
    if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) {
        lastRenderedItem = lastVirtualItem ? list[lastVirtualItem.index] : undefined;
        firstRenderedItem = firstVirtualItem ? list[firstVirtualItem.index] : undefined;
    } else {
        const lastChronologicalIdx =
            lastVirtualItem === undefined ? undefined : toListIndex(lastVirtualItem.index);
        lastRenderedItem =
            lastChronologicalIdx === undefined
                ? list[chronologicalEndIndex - chronologicalStartIndex]
                : list[lastChronologicalIdx];
        const firstChronologicalIdx =
            firstVirtualItem === undefined ? undefined : toListIndex(firstVirtualItem.index);
        firstRenderedItem =
            firstChronologicalIdx === undefined ? list[0] : list[firstChronologicalIdx];
    }

    if (!lastRenderedItem || !firstRenderedItem) return null;

    // Capture the narrowed values into `const` so the closures below keep the
    // non-undefined narrowing (TS re-widens `let` variables inside closures).
    const firstItem = firstRenderedItem;
    const lastItem = lastRenderedItem;

    const isAboveViewport = (unreadId: number): boolean =>
        isAsc ? firstItem.id > unreadId : firstItem.id < unreadId;
    const isBelowViewport = (unreadId: number): boolean =>
        isAsc ? lastItem.id < unreadId : lastItem.id > unreadId;

    const showTopUrgentButton =
        unreadInfos.HIGH_PRIORITY_UPDATE && isAboveViewport(unreadInfos.HIGH_PRIORITY_UPDATE.id);
    const showTopNewButton =
        !showTopUrgentButton &&
        unreadInfos.STANDARD_UPDATE &&
        isAboveViewport(unreadInfos.STANDARD_UPDATE.id);
    const showBottomUrgentButton =
        unreadInfos.HIGH_PRIORITY_UPDATE && isBelowViewport(unreadInfos.HIGH_PRIORITY_UPDATE.id);
    const showBottomNewButton =
        !showBottomUrgentButton &&
        unreadInfos.STANDARD_UPDATE &&
        isBelowViewport(unreadInfos.STANDARD_UPDATE.id);

    const scrollToUrgentInfo = (): void => {
        unreadInfos?.HIGH_PRIORITY_UPDATE?.id !== undefined &&
            setScrollTargetInfoId(unreadInfos.HIGH_PRIORITY_UPDATE.id);
    };

    const scrollToNewInfo = (): void => {
        unreadInfos?.STANDARD_UPDATE?.id !== undefined &&
            setScrollTargetInfoId(unreadInfos.STANDARD_UPDATE.id);
    };

    return (
        <div className={styles['parent']}>
            {showTopUrgentButton && (
                <UnreadArrowButton
                    position="top"
                    urgent
                    onClick={scrollToUrgentInfo}
                    data-testid="URGENT_UPDATE_ABOVE_BUTTON"
                >
                    Urgent Update
                </UnreadArrowButton>
            )}
            {showTopNewButton && (
                <UnreadArrowButton
                    position="top"
                    onClick={scrollToNewInfo}
                    data-testid="NEW_MESSAGE_ABOVE_BUTTON"
                >
                    New Message
                </UnreadArrowButton>
            )}
            {showBottomUrgentButton && (
                <UnreadArrowButton
                    position="bottom"
                    urgent
                    onClick={scrollToUrgentInfo}
                    data-testid="URGENT_UPDATE_BELOW_BUTTON"
                >
                    Urgent Update
                </UnreadArrowButton>
            )}
            {showBottomNewButton && (
                <UnreadArrowButton
                    position="bottom"
                    onClick={scrollToNewInfo}
                    data-testid="NEW_MESSAGE_BELOW_BUTTON"
                >
                    New Message
                </UnreadArrowButton>
            )}
        </div>
    );
});
